import { useMessages } from '@features/l10n/l10nInjector.js';
import { Text } from '@features/l10n/Text.js';
import { hasLegend } from '@features/legend/legendLayers.js';
import {
  drawnTypesSelector,
  libraryIndexSelector,
  resolvedCustomLayersSelector,
} from '@features/mapLibrary/model/selectors.js';
import { Chord } from '@shared/components/Chord.js';
import { FmDropdownMenu } from '@shared/components/FmDropdownMenu.js';
import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import { MenuGutter } from '@shared/components/MenuGutter.js';
import { MenuLabel } from '@shared/components/MenuLabel.js';
import { OnlineOnlyItem } from '@shared/components/OnlineOnlyItem.js';
import { useModalLink } from '@shared/components/ShowModalLink.js';
import { Toolbar } from '@shared/components/Toolbar.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import type { ReactElement } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  FaFileContract,
  FaInfoCircle,
  FaList,
  FaLock,
  FaMoneyBillWave,
  FaRegCopyright,
} from 'react-icons/fa';
import { useAttributionInfo } from './useAttributionInfo.js';

export function CopyrightButton(): ReactElement {
  const m = useMessages();

  const customLayers = useAppSelector(resolvedCustomLayersSelector);

  const layers = useAppSelector(drawnTypesSelector);

  const libraryIndex = useAppSelector(libraryIndexSelector);

  const showLegendButton = hasLegend(layers, libraryIndex, customLayers);

  const modalLink = useModalLink();

  const showAttribution = useAttributionInfo();

  return (
    <Toolbar className="me-2 mb-2">
      <Dropdown>
        <LongPressTooltip label={m?.general.infoAndLegal}>
          {({ props }) => (
            <Dropdown.Toggle
              bsPrefix="fm-dropdown-toggle-nocaret"
              id="dropdown-basic"
              variant="secondary"
              {...props}
            >
              <FaInfoCircle />
            </Dropdown.Toggle>
          )}
        </LongPressTooltip>

        <FmDropdownMenu style={{ width: 'max-content' }}>
          <Dropdown.Item
            key="attribution"
            as="button"
            onClick={() => showAttribution()}
          >
            <FaRegCopyright />
            <MenuLabel>
              <Text k="main.copyright" />
            </MenuLabel>
          </Dropdown.Item>

          {showLegendButton && (
            <OnlineOnlyItem {...modalLink({ type: 'legend' })}>
              <FaList />
              <MenuLabel>
                <Text k="mainMenu.mapLegend" />
              </MenuLabel>
              <MenuGutter>
                <Chord modal="legend" />
              </MenuGutter>
            </OnlineOnlyItem>
          )}

          <Dropdown.Item
            key="privacyPolicy"
            {...modalLink({ type: 'document', key: 'privacyPolicy' })}
          >
            <FaLock />
            <MenuLabel>
              <Text k="general.privacyPolicy" />
            </MenuLabel>
          </Dropdown.Item>

          <Dropdown.Item
            key="termsOfService"
            {...modalLink({ type: 'document', key: 'termsOfService' })}
          >
            <FaFileContract />
            <MenuLabel>
              <Text k="general.termsOfService" />
            </MenuLabel>
          </Dropdown.Item>

          {/* Terms of a purchase, and an embed sells nothing — its premium gem
              leaves for the portal, which states them where the money is
              actually taken. The two policies above stay: the embed does
              process the visitor's data, and they are using the service. */}
          {!window.fmEmbedded && (
            <Dropdown.Item
              key="refundPolicy"
              {...modalLink({ type: 'document', key: 'refundPolicy' })}
            >
              <FaMoneyBillWave />
              <MenuLabel>
                <Text k="general.refundPolicy" />
              </MenuLabel>
            </Dropdown.Item>
          )}
        </FmDropdownMenu>
      </Dropdown>
    </Toolbar>
  );
}
